import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICreatePolicy, IPermission, IPolicy, IService } from '@shared/models/scim-admin';
import { NotificationService } from '@shared/services/notification.service';
import { forkJoin, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ScimAdminService } from '../scim-admin.service';

export interface IPermissionServiceGroup {
	service: IService;
	permissions: IPermission[];
}

@Component({
	selector: 'app-update-policy-dialog',
	standalone: false,
	templateUrl: './update-policy-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./update-policy-dialog.component.scss'],
})
export class UpdatePolicyDialogComponent implements OnInit, OnDestroy {
	@Output() updated = new EventEmitter<any>();
	private destroy$ = new Subject<void>();

	form: FormGroup;
	isLoading = false;
	isLoadingPermissions = true;
	permissionGroups: IPermissionServiceGroup[] = [];
	selectedPermissions = new Set<string>();

	constructor(
		@Inject(MAT_DIALOG_DATA) public policy: IPolicy,
		public dialogRef: MatDialogRef<UpdatePolicyDialogComponent>,
		private scimAdminService: ScimAdminService,
		private fb: FormBuilder,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.initializeForm();
		this.loadServicesAndPermissions();
		// Pre-select existing permissions
		if (this.policy.permissions) {
			this.policy.permissions.forEach((p) => this.selectedPermissions.add(p));
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: [this.policy.name, [Validators.required, Validators.maxLength(60)]],
			description: [this.policy.description || ''],
		});
	}

	private loadServicesAndPermissions(): void {
		this.scimAdminService
			.getServices()
			.pipe(take(1))
			.subscribe({
				next: (services) => {
					if (services.length === 0) {
						this.isLoadingPermissions = false;
						return;
					}
					const permissionRequests = services.map((s) => this.scimAdminService.getServicePermissions(s.id));
					forkJoin(permissionRequests)
						.pipe(take(1))
						.subscribe({
							next: (allPermissions) => {
								this.permissionGroups = services
									.map((service, index) => ({
										service,
										permissions: allPermissions[index] || [],
									}))
									.filter((g) => g.permissions.length > 0);
								this.isLoadingPermissions = false;
							},
							error: () => {
								this.isLoadingPermissions = false;
							},
						});
				},
				error: () => {
					this.isLoadingPermissions = false;
				},
			});
	}

	togglePermission(permissionId: string): void {
		if (this.selectedPermissions.has(permissionId)) {
			this.selectedPermissions.delete(permissionId);
		} else {
			this.selectedPermissions.add(permissionId);
		}
	}

	isPermissionSelected(permissionId: string): boolean {
		return this.selectedPermissions.has(permissionId);
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onSave(): void {
		if (this.isLoading || this.form.invalid) return;

		this.isLoading = true;

		const data: ICreatePolicy = {
			name: this.form.value.name,
			description: this.form.value.description || null,
			permissions: Array.from(this.selectedPermissions),
		};

		const request$ = this.policy.type === 'organization' ? this.scimAdminService.updateOrganizationPolicy(this.policy.id, data) : this.scimAdminService.updateAccountPolicy(this.policy.id, data);

		request$.pipe(take(1)).subscribe({
			next: (updatedPolicy) => {
				this.notificationService.notify(['scim-administration.policies.notifications.updateSuccess.header'], ['scim-administration.policies.notifications.updateSuccess.content'], 'success');
				this.updated.emit(updatedPolicy);
				this.dialogRef.close(updatedPolicy);
			},
			error: (error) => {
				this.isLoading = false;
				this.notificationService.notify(['scim-administration.policies.notifications.updateError.header'], [error?.error?.error?.message || 'scim-administration.policies.notifications.updateError.content'], 'error');
			},
		});
	}
}
